import React from 'react';
import { toast } from 'sonner';

import type { ConfigFieldValue } from '@/components/services';
import {
  enhancedApi,
  type FirmServiceWorkflowSchema,
  type WorkflowConfigSchema,
} from '@/services/api/generated/firmsService';
import { getApiErrorMessage } from '@/utils/api-error';

import { ConfigField } from './ConfigField';

interface WorkflowConfigEditorProps {
  firmId: string;
  serviceId: string;
  workflow: FirmServiceWorkflowSchema;
  disabled?: boolean;
}

/** Type guard for valid config field values */
const isValidConfigValue = (value: unknown): value is ConfigFieldValue => {
  if (value === null || value === undefined) return true;
  if (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    typeof value === 'number'
  ) {
    return true;
  }
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return true;
  }
  return false;
};

/**
 * Generic workflow configuration editor.
 *
 * Renders configuration fields based on backend-provided metadata.
 * Each workflow's config_fields array describes what UI to render.
 */
export const WorkflowConfigEditor: React.FC<WorkflowConfigEditorProps> = ({
  firmId,
  serviceId,
  workflow,
  disabled = false,
}) => {
  const [updateWorkflows, { isLoading }] =
    enhancedApi.useBulkUpdateFirmServiceWorkflowsMutation();

  if (!workflow.config_fields?.length) {
    return null;
  }

  const handleFieldChange = async (
    fieldKey: string,
    newValue: ConfigFieldValue,
  ) => {
    const currentConfig = workflow.config ?? {};
    const updatedConfig = {
      ...currentConfig,
      [fieldKey]: newValue,
    } as WorkflowConfigSchema['config'];

    try {
      await updateWorkflows({
        firmId,
        firmServiceId: serviceId,
        updateFirmServiceWorkflowsRequest: {
          workflows: [
            {
              workflow_code: workflow.workflow_code,
              is_enabled: workflow.is_enabled,
              notes: workflow.notes ?? '',
              config: updatedConfig,
            },
          ],
        },
      }).unwrap();

      toast.success('Configuration updated');
    } catch (error) {
      toast.error(
        `Failed to update configuration: ${getApiErrorMessage(error)}`,
      );
    }
  };

  const getFieldValue = (fieldKey: string): ConfigFieldValue => {
    if (!workflow.config) return undefined;
    const value = (workflow.config as Record<string, unknown>)[fieldKey];
    return isValidConfigValue(value) ? value : undefined;
  };

  return (
    <div className="space-y-2">
      {workflow.config_fields.map((field) => (
        <ConfigField
          key={field.key}
          field={field}
          value={getFieldValue(field.key)}
          onChange={(value) => handleFieldChange(field.key, value)}
          disabled={disabled}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};
